/**
 * FloatingEmbers.tsx — a drifting-ember particle canvas (BETA 22.50).
 *
 * Extracted from IntroSequence so the front-facing Sorry Boss menu and the
 * intro's freeze-frame share ONE ember effect — Dylan: "effects floating
 * around it to keep the background alive." Fills its parent (position it with
 * an absolutely-positioned wrapper). Pointer-events off; purely decorative.
 */
import { useEffect, useRef } from 'react';

export default function FloatingEmbers({ count = 46 }: { count?: number }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;
    let raf = 0, w = 0, h = 0;
    const resize = () => { w = cv.width = cv.clientWidth; h = cv.height = cv.clientHeight; };
    resize();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    ro?.observe(cv);
    type P = { x: number; y: number; r: number; vy: number; vx: number; a: number; tw: number; hue: number };
    const N = count;
    const parts: P[] = Array.from({ length: N }, () => ({
      x: Math.random() * (w || 1280), y: Math.random() * (h || 720),
      r: 0.8 + Math.random() * 2.4, vy: -(6 + Math.random() * 16), vx: (Math.random() - 0.5) * 8,
      a: 0.15 + Math.random() * 0.5, tw: Math.random() * Math.PI * 2, hue: 28 + Math.random() * 22,
    }));
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000); last = now;
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'lighter';
      for (const p of parts) {
        p.y += p.vy * dt; p.x += p.vx * dt; p.tw += dt * 2.5;
        if (p.y < -8) { p.y = h + 8; p.x = Math.random() * w; }
        if (p.x < -8) p.x = w + 8; else if (p.x > w + 8) p.x = -8;
        const flick = 0.55 + 0.45 * Math.sin(p.tw);
        const alpha = p.a * flick;
        const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 4);
        g.addColorStop(0, `hsla(${p.hue},95%,65%,${alpha})`);
        g.addColorStop(1, `hsla(${p.hue},95%,55%,0)`);
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r * 4, 0, Math.PI * 2); ctx.fill();
      }
      ctx.globalCompositeOperation = 'source-over';
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); ro?.disconnect(); };
  }, [count]);
  return <canvas ref={ref} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />;
}
